import React, { useEffect, useMemo, useState } from "react";
import { useForm, useFieldArray, useWatch, Controller } from "react-hook-form";
import ReactSelect from "react-select";
import { useNavigate } from "react-router-dom";
import {
  Plus, Trash2, AlertCircle, Loader2, Package, Hash, Ruler, Weight, Scissors,
} from "lucide-react";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  getProcessOrderPartyOptions,
  getProcessOrderSubProcessOptions,
  getProcessOrderItemOptions,
  getProcessOrderItemSizeOptions,
  getProcessOrderItemCuttingSizeOptions,
  getProcessOrderMaterialOptions,
  getProcessOrderMaterialSizeOptions,
  getProcessOrderLeftovers,
  fetchProcessOrderParties,
  fetchProcessOrderSubProcesses,
  fetchProcessOrderItems,
  fetchProcessOrderItemSizes,
  fetchProcessOrderItemCuttingSizes,
  fetchProcessOrderMaterials,
  fetchProcessOrderMaterialSizes,
  fetchProcessOrderLeftovers,
} from "../../features/processOrders/processOrdersSlice";
import { IProcessOrderFormInput } from "../../types/processOrder";
import { ProcessDefinitionProps } from "../../types/processDefinition";
import { useCuttingMaterialGroups } from "../../hooks/useCuttingMaterialGroups";
import MaterialGroupsSection from "./MaterialGroupsSection";
import { POST, PUT } from "../../services/axios.service";
import { toastStyles, filterSelectStyles } from "../../static/styles";
import { nowInIstForInput } from "../../static/datetime";
import { allowedModes, defaultMode as pickDefaultMode } from "../../lib/utils";
import { t } from "../../locales";
import { route } from "../../static/route";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";

type Props = {
  processDefinition: ProcessDefinitionProps;
  editId?: number;
  defaultValues?: IProcessOrderFormInput;
  defaultPartyOption?: { value: number; label: string } | null;
  defaultMode?: "in_house" | "outsource";
  // Only meaningful when processDefinition.handler_key === "casting".
  editType?: string | null;
};

const inputClass = (hasError: boolean) =>
  `pl-12 h-12 text-base border-2 rounded-lg transition-all duration-200 ${
    hasError
      ? "border-red-500 focus:border-red-500 focus:shadow-focus-error"
      : "border-gray-200 focus:border-[#113F67] focus:shadow-focus-brand"
  }`;

const itemInputClass = (hasError: boolean) =>
  `pl-10 h-[48px] text-base border-2 rounded-lg transition-all duration-200 ${
    hasError
      ? "border-red-500 focus:border-red-500 focus:shadow-focus-error"
      : "border-gray-200 focus:border-[#113F67] focus:shadow-focus-brand"
  }`;

const formSelectStyles: any = {
  ...filterSelectStyles,
  control: (base: any, state: any) => ({
    ...(typeof filterSelectStyles.control === "function" ? (filterSelectStyles.control as any)(base, state) : base),
    borderRadius: "0.5rem",
    fontSize: "1rem",
  }),
  singleValue: (base: any) => ({
    ...(typeof filterSelectStyles.singleValue === "function" ? (filterSelectStyles.singleValue as any)(base, {}) : base),
    fontSize: "1rem",
  }),
  placeholder: (base: any) => ({
    ...(typeof filterSelectStyles.placeholder === "function" ? (filterSelectStyles.placeholder as any)(base, {}) : base),
    fontSize: "1rem",
  }),
  input: (base: any) => ({ ...base, fontSize: "1rem" }),
};

const formSelectErrorStyles: any = {
  ...formSelectStyles,
  control: (base: any, state: any) => ({
    ...(typeof formSelectStyles.control === "function" ? formSelectStyles.control(base, state) : base),
    borderColor: "#ef4444",
    boxShadow: state.isFocused ? "0 0 0 2px #fff, 0 0 0 4px #ef4444" : "none",
    "&:hover": { borderColor: "#ef4444" },
  }),
};

const emptyItem = { sub_process_id: 0, item_id: 0, item_size_id: 0, cutting_size: "", material_id: 0, size_id: 0, weight: "", qty: "" };

const ProcessOrderForm = ({ processDefinition, editId, defaultValues, defaultPartyOption, defaultMode, editType }: Props) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const labels = t.processOrders;
  const apiPath = "/v1/process-orders";
  const listRoute = `${route.processOrders}?process=${processDefinition.slug}`;
  const requiresWeight = processDefinition.rate_basis === "weight";
  // Cutting is always in-house — leftover pipes come from a shared no-party
  // pool, so a party on a cutting order silently empties that pool and cuts
  // all-fresh pipes with no error. See ProcessOrdersController::buildCuttingOrderItems().
  const isCutting = processDefinition.handler_key === "cutting";
  const isCasting = processDefinition.handler_key === "casting";

  const [selectedType, setSelectedType] = useState<"aluminium" | "white_metal">(
    (editType as "aluminium" | "white_metal") || "aluminium"
  );
  // Aluminium casting is raw-material intake, not a costed job: no weight, no
  // size dropdown (auto-derived from the raw-material item's own material),
  // no rate/amount. See ProcessOrdersController::isAluminiumCasting().
  const isAluminiumCasting = isCasting && selectedType === "aluminium";
  // Every non-aluminium row picks a real Item, and its material is always
  // that item's own material — shown read-only, never picked independently.
  const derivesMaterialFromItem = (requiresWeight || isCutting) && !isAluminiumCasting;
  const baseAllowedModes = useMemo(
    () => allowedModes(processDefinition.mode_policy),
    [processDefinition.mode_policy]
  );
  const [mode, setMode] = useState<"in_house" | "outsource">(
    defaultMode ?? pickDefaultMode(baseAllowedModes)
  );
  const [partyError, setPartyError] = useState(false);

  const partyOptions = useAppSelector(getProcessOrderPartyOptions);
  const subProcessOptions = useAppSelector(getProcessOrderSubProcessOptions);
  const itemOptions = useAppSelector(getProcessOrderItemOptions);
  const itemSizeOptions = useAppSelector(getProcessOrderItemSizeOptions);
  // Cutting only — remembered cutting size per (item_id, item_size_id) pair,
  // looked up client-side to prefill the Cutting Size field.
  const itemCuttingSizeOptions = useAppSelector(getProcessOrderItemCuttingSizeOptions);
  // Cascading raw-material picker: Field A (Material) narrows Field B
  // (Material Size, the actual submitted size_id) to that material's own
  // sizes. Used for Casting's raw material (Aluminium + White Metal) and for
  // Cutting's Material Size field — every place the old single Sizes
  // dropdown used to live.
  const materialOptions = useAppSelector(getProcessOrderMaterialOptions);
  const materialSizeOptions = useAppSelector(getProcessOrderMaterialSizeOptions);
  // When a material only really has one size worth using, pre-select it
  // instead of leaving the size dropdown empty after a material change.
  const defaultSizeIdFor = (materialId: number) => {
    const defaultOpt = materialSizeOptions.find((o) => o.material_id === materialId && o.is_default);
    return defaultOpt?.value || 0;
  };
  // Cutting only — the cutting size last saved for this exact (item, item
  // size) pair, if any. Empty string when the pair hasn't been cut before
  // (or either half isn't picked yet), leaving the field for fresh entry.
  const cuttingSizeFor = (itemId: number, itemSizeId: number): number | "" => {
    if (!itemId || !itemSizeId) return "";
    const found = itemCuttingSizeOptions.find((o) => o.item_id === itemId && o.item_size_id === itemSizeId);
    return found ? found.cutting_size : "";
  };
  const dbLeftovers = useAppSelector(getProcessOrderLeftovers);

  const [isLoading, setIsLoading] = useState(false);
  const [selectedParty, setSelectedParty] = useState<{ value: number; label: string } | null>(
    defaultPartyOption || null
  );

  const {
    handleSubmit,
    register,
    control,
    setValue,
    formState: { errors },
  } = useForm<IProcessOrderFormInput>({
    defaultValues: defaultValues || {
      party_id: 0,
      date: nowInIstForInput(),
      items: [emptyItem],
    } as any,
  });

  const { fields, append, remove } = useFieldArray({ control, name: "items" });
  const watchedItems = useWatch({ control, name: "items" });

  // Every sub-process a line currently references narrows the header mode
  // toggle: a sub-process locked to one mode flips the toggle to it and
  // locks it there, and two rows locked to different modes is a conflict
  // that must block save (mirrors ProcessOrdersController::validateModeAndRates()).
  const selectedSubProcessIds = useMemo(
    () =>
      Array.from(
        new Set(
          (watchedItems || [])
            .map((it: any) => Number(it?.sub_process_id) || 0)
            .filter((id: number) => id > 0)
        )
      ),
    [watchedItems]
  );
  const effectiveAllowedModes = useMemo(() => {
    let allowed = baseAllowedModes;
    for (const subProcessId of selectedSubProcessIds) {
      const subProcessPolicy = subProcessOptions.find((o) => o.value === subProcessId)?.mode_policy;
      const rowAllowed = allowedModes(processDefinition.mode_policy, subProcessPolicy);
      allowed = allowed.filter((m) => rowAllowed.includes(m));
    }
    return allowed;
  }, [baseAllowedModes, selectedSubProcessIds, subProcessOptions, processDefinition.mode_policy]);
  const subProcessModeConflict = effectiveAllowedModes.length === 0;
  const modeLockedBySubProcess = selectedSubProcessIds.length > 0 && effectiveAllowedModes.length === 1;

  useEffect(() => {
    if (effectiveAllowedModes.length === 1 && mode !== effectiveAllowedModes[0]) {
      setMode(effectiveAllowedModes[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveAllowedModes.join(",")]);

  const {
    materialGroups,
    groupSelectedLeftovers,
    getAvailableLeftoversForGroup,
    toggleGroupLeftover,
    buildMaterialGroupsPayload,
  } = useCuttingMaterialGroups({
    enabled: isCutting,
    watchedItems,
    itemOptions,
    materialOptions,
    materialSizeOptions,
    dbLeftovers,
  });

  useEffect(() => {
    dispatch(fetchProcessOrderParties(processDefinition.id));
    dispatch(fetchProcessOrderSubProcesses(processDefinition.id));
    dispatch(fetchProcessOrderItems());
    dispatch(fetchProcessOrderItemSizes());
    dispatch(fetchProcessOrderMaterials());
    // Unfiltered — every material's sizes load once, then each row's Material
    // Size dropdown filters this list client-side by its own material_id.
    dispatch(fetchProcessOrderMaterialSizes(undefined));
    if (isCutting) {
      dispatch(fetchProcessOrderLeftovers({}));
      dispatch(fetchProcessOrderItemCuttingSizes());
    }
  }, [dispatch, processDefinition.id, isCutting]);

  useEffect(() => {
    if (defaultPartyOption) setSelectedParty(defaultPartyOption);
  }, [defaultPartyOption]);

  const onSubmit = async (data: IProcessOrderFormInput) => {
    if (subProcessModeConflict) {
      return;
    }
    if (mode === "outsource" && !selectedParty) {
      setPartyError(true);
      return;
    }
    setPartyError(false);
    setIsLoading(true);

    try {
      const payload: Record<string, unknown> = {
        process_definition_id: processDefinition.id,
        mode,
        party_id: mode === "outsource" ? (selectedParty?.value || null) : null,
        date: data.date,
        items: data.items.map((it) => ({
          sub_process_id: Number(it.sub_process_id) || null,
          item_id: isAluminiumCasting ? null : (Number(it.item_id) || null),
          item_size_id: isAluminiumCasting ? null : (Number(it.item_size_id) || null),
          cutting_size: isCutting ? (Number(it.cutting_size) || null) : null,
          size_id: Number(it.size_id) || null,
          weight: requiresWeight && !isAluminiumCasting ? (Number(it.weight) || 0) : null,
          qty: Number(it.qty) || 0,
        })),
      };

      if (isCutting) {
        payload.material_groups = buildMaterialGroupsPayload();
      }
      if (isCasting) {
        payload.type = selectedType;
      }

      const response: any = editId
        ? await PUT(`${apiPath}/${editId}`, payload)
        : await POST(apiPath, payload);

      if (response.data.status === "success") {
        toast.success(response.data.message, { ...(toastStyles as any) });
        navigate(listRoute);
      } else {
        toast.error(response.data.message, { ...(toastStyles as any) });
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || t.common.somethingWentWrong,
        { ...(toastStyles as any) }
      );
    }
    setIsLoading(false);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <Card className="shadow-lg border-0 bg-white/80 backdrop-blur-sm">
        <CardContent className="pt-6 space-y-6">
          <div className={isCutting ? "flex flex-col md:flex-row gap-6" : "grid grid-cols-1 md:grid-cols-3 gap-6"}>
            {/* Mode — locked when the process (or a selected sub-process) only supports one
                mode (e.g. cutting's shared no-party leftover pool is in-house only) */}
            <div className="space-y-2 md:col-span-1">
              <Label className="text-sm font-semibold text-[#113F67]">{labels.modeLabel}</Label>
              <div className="flex rounded-lg border-2 border-gray-200 overflow-hidden h-12">
                <button
                  type="button"
                  disabled={!effectiveAllowedModes.includes("in_house")}
                  className={`flex-1 text-sm font-medium transition-colors ${mode === "in_house" ? "bg-[#113F67] text-white" : "bg-white text-[#113F67]"} ${!effectiveAllowedModes.includes("in_house") ? "opacity-40 cursor-not-allowed" : ""}`}
                  onClick={() => effectiveAllowedModes.includes("in_house") && setMode("in_house")}
                >
                  {labels.modeInHouse}
                </button>
                <button
                  type="button"
                  disabled={!effectiveAllowedModes.includes("outsource")}
                  className={`flex-1 text-sm font-medium transition-colors ${mode === "outsource" ? "bg-[#113F67] text-white" : "bg-white text-[#113F67]"} ${!effectiveAllowedModes.includes("outsource") ? "opacity-40 cursor-not-allowed" : ""}`}
                  onClick={() => effectiveAllowedModes.includes("outsource") && setMode("outsource")}
                >
                  {labels.modeOutsource}
                </button>
              </div>
              {subProcessModeConflict ? (
                <p className="text-xs text-red-500 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />{labels.subProcessModeConflict}
                </p>
              ) : modeLockedBySubProcess ? (
                <p className="text-xs text-gray-500">{labels.modeLockedBySubProcess}</p>
              ) : !baseAllowedModes.includes("outsource") ? (
                <p className="text-xs text-gray-500">{labels.modeLockedHint}</p>
              ) : !baseAllowedModes.includes("in_house") ? (
                <p className="text-xs text-gray-500">{labels.modeLockedOutsourceHint}</p>
              ) : null}
            </div>

            {/* Party — only collected for outsource work */}
            {mode === "outsource" && (
              <div className="space-y-2 md:col-span-2">
                <Label className="text-sm font-semibold text-[#113F67]">
                  {labels.partyLabel} <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <Package className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#34699A] z-10 pointer-events-none" />
                  <ReactSelect
                    styles={partyError ? formSelectErrorStyles : formSelectStyles}
                    options={partyOptions}
                    value={selectedParty}
                    onChange={(opt: any) => {
                      setSelectedParty(opt);
                      if (opt) setPartyError(false);
                    }}
                    placeholder={labels.selectParty}
                    isClearable
                  />
                </div>
                {partyError && (
                  <p className="text-sm text-red-500 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />{labels.partyRequiredOutsource}
                  </p>
                )}
              </div>
            )}

            {/* Date */}
            <div className={isCutting ? "space-y-2 w-full md:w-64" : "space-y-2 md:col-span-1"}>
              <Label className="text-sm font-semibold text-[#113F67]">
                {labels.dateLabel} <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Input
                  type="datetime-local"
                  className={`${inputClass(!!errors.date)} [&::-webkit-calendar-picker-indicator]:hidden`}
                  style={{ paddingLeft: "1rem" }}
                  {...register("date", { required: labels.dateRequired })}
                  onClick={(e) => (e.target as HTMLInputElement).showPicker?.()}
                />
              </div>
              {errors.date && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />{errors.date.message as string}
                </p>
              )}
            </div>

            {/* Type — casting only; drives whether items carry an item_id (white_metal) or just a size (aluminium) */}
            {isCasting && (
              <div className="space-y-2 md:col-span-1">
                <Label className="text-sm font-semibold text-[#113F67]">
                  {labels.typeLabel} <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <Package className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#34699A] z-10 pointer-events-none" />
                  <ReactSelect
                    styles={formSelectStyles}
                    options={[
                      { value: "aluminium", label: labels.typeAluminium },
                      { value: "white_metal", label: labels.typeWhiteMetal },
                    ]}
                    value={{
                      value: selectedType,
                      label: selectedType === "aluminium" ? labels.typeAluminium : labels.typeWhiteMetal,
                    }}
                    onChange={(opt: any) => setSelectedType(opt?.value || "aluminium")}
                    placeholder={labels.selectType}
                    isClearable={false}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="border-t-2 border-[#113F67]/10" />

          {/* Items */}
          <div>
            <div className="mb-4">
              <Label className="text-lg font-bold text-[#113F67]">{labels.itemsLabel}</Label>
            </div>

            {fields.length === 0 && (
              <p className="text-center text-gray-500 py-8">{labels.noItemsText}</p>
            )}

            {fields.map((field, index) => (
              <div
                key={field.id}
                className="mb-4 rounded-xl p-4 md:p-5 bg-gray-50/50 border-l-4 border-[#34699A] shadow-sm"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base font-bold text-[#113F67] flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#113F67] text-white text-xs font-bold">{index + 1}</span>
                    {labels.itemLabel} {index + 1}
                  </h3>
                  {fields.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => remove(index)}
                    >
                      <Trash2 className="h-4 w-4 mr-1" /> {labels.removeItem}
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  {/* Aluminium casting never touches Item/Item Size at all — it only
                      ever picks a raw material, via a real cascading pair: Material
                      (name only), then Material Size (a real number) scoped to it.
                      Every other process keeps the normal Item field. */}
                  {isAluminiumCasting ? (
                    <>
                      <div className="space-y-1.5">
                        <Label className="text-xs font-semibold text-[#113F67]">
                          {labels.rawMaterialLabel} <span className="text-red-500">*</span>
                        </Label>
                        <div className="relative">
                          <Package className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#34699A] z-10 pointer-events-none" />
                          <Controller
                            name={`items.${index}.material_id`}
                            control={control}
                            render={({ field }) => (
                              <ReactSelect
                                styles={formSelectStyles}
                                options={materialOptions}
                                value={materialOptions.find((o) => o.value === Number(field.value)) || null}
                                onChange={(opt: any) => {
                                  field.onChange(opt?.value || 0);
                                  // Switching material invalidates the previously
                                  // chosen size — it may not belong to this material.
                                  // Fall back to that material's default size when it has one.
                                  setValue(`items.${index}.size_id`, defaultSizeIdFor(opt?.value || 0));
                                }}
                                placeholder={labels.selectMaterial}
                                isClearable
                              />
                            )}
                          />
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-xs font-semibold text-[#113F67]">
                          {labels.rawMaterialSizeLabel} <span className="text-red-500">*</span>
                        </Label>
                        <div className="relative">
                          <Ruler className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#34699A] z-10 pointer-events-none" />
                          <Controller
                            name={`items.${index}.size_id`}
                            control={control}
                            rules={{ validate: (v) => Number(v) > 0 || labels.rawMaterialRequired }}
                            render={({ field }) => {
                              const materialId = Number(watchedItems?.[index]?.material_id) || 0;
                              const scopedOptions = materialSizeOptions.filter((o) => o.material_id === materialId);
                              return (
                                <ReactSelect
                                  styles={errors.items?.[index]?.size_id ? formSelectErrorStyles : formSelectStyles}
                                  options={scopedOptions}
                                  value={scopedOptions.find((o) => o.value === Number(field.value)) || null}
                                  onChange={(opt: any) => field.onChange(opt?.value || 0)}
                                  placeholder={materialId ? labels.selectRawMaterialSize : labels.selectMaterialFirst}
                                  isClearable
                                  isDisabled={!materialId}
                                />
                              );
                            }}
                          />
                        </div>
                        {errors.items?.[index]?.size_id && (
                          <p className="text-xs text-red-500 flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />{labels.rawMaterialRequired}
                          </p>
                        )}
                      </div>
                    </>
                  ) : (
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-[#113F67]">
                      {labels.itemLabel} <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <Package className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#34699A] z-10 pointer-events-none" />
                      <Controller
                        name={`items.${index}.item_id`}
                        control={control}
                        rules={{ validate: (v) => Number(v) > 0 || labels.itemRequired }}
                        render={({ field }) => (
                          <ReactSelect
                            styles={errors.items?.[index]?.item_id ? formSelectErrorStyles : formSelectStyles}
                            options={itemOptions}
                            value={itemOptions.find((o) => o.value === Number(field.value)) || null}
                            onChange={(opt: any) => {
                              field.onChange(opt?.value || 0);
                              // Material is no longer picked independently for
                              // cutting or weight-basis casting — it's always the
                              // item's own material, shown read-only below.
                              if (derivesMaterialFromItem) {
                                if (opt?.material_size_id) {
                                  setValue(`items.${index}.material_id`, opt.material_id || 0);
                                  setValue(`items.${index}.size_id`, opt.material_size_id);
                                } else {
                                  setValue(`items.${index}.material_id`, 0);
                                  setValue(`items.${index}.size_id`, 0);
                                  if (opt) {
                                    toast.error(labels.itemNoMaterialAssigned, { ...(toastStyles as any) });
                                  }
                                }
                              }
                              if (isCutting) {
                                const itemSizeId = Number(watchedItems?.[index]?.item_size_id) || 0;
                                setValue(`items.${index}.cutting_size`, cuttingSizeFor(opt?.value || 0, itemSizeId));
                              }
                            }}
                            placeholder={labels.selectItem}
                            isClearable
                          />
                        )}
                      />
                    </div>
                    {errors.items?.[index]?.item_id && (
                      <p className="text-xs text-red-500 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />{labels.itemRequired}
                      </p>
                    )}
                  </div>
                  )}

                  {/* Item Size — not applicable to aluminium casting's raw-material lines */}
                  {!isAluminiumCasting && (
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-[#113F67]">
                        {labels.itemSizeLabel} {isCutting && <span className="text-red-500">*</span>}
                      </Label>
                      <div className="relative">
                        <Ruler className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#34699A] z-10 pointer-events-none" />
                        <Controller
                          name={`items.${index}.item_size_id`}
                          control={control}
                          rules={isCutting ? { validate: (v) => Number(v) > 0 || labels.itemSizeRequired } : undefined}
                          render={({ field }) => (
                            <ReactSelect
                              styles={errors.items?.[index]?.item_size_id ? formSelectErrorStyles : formSelectStyles}
                              options={itemSizeOptions}
                              value={itemSizeOptions.find((o) => o.value === Number(field.value)) || null}
                              onChange={(opt: any) => {
                                field.onChange(opt?.value || 0);
                                if (isCutting) {
                                  const itemId = Number(watchedItems?.[index]?.item_id) || 0;
                                  setValue(`items.${index}.cutting_size`, cuttingSizeFor(itemId, opt?.value || 0));
                                }
                              }}
                              placeholder={labels.selectItemSize}
                              isClearable
                            />
                          )}
                        />
                      </div>
                      {errors.items?.[index]?.item_size_id && (
                        <p className="text-xs text-red-500 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />{labels.itemSizeRequired}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Sub Process (only if this process has any) */}
                  {subProcessOptions.length > 0 && (
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-[#113F67]">{labels.subProcessLabel}</Label>
                      <div className="relative">
                        <Package className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#34699A] z-10 pointer-events-none" />
                        <Controller
                          name={`items.${index}.sub_process_id`}
                          control={control}
                          render={({ field }) => (
                            <ReactSelect
                              styles={formSelectStyles}
                              options={subProcessOptions}
                              value={subProcessOptions.find((o) => o.value === Number(field.value)) || null}
                              onChange={(opt: any) => field.onChange(opt?.value || 0)}
                              placeholder={labels.selectSubProcess}
                              isClearable
                            />
                          )}
                        />
                      </div>
                    </div>
                  )}

                  {/* Material — read-only, derived from the row's own Item (its
                      material_id → material_sizes → materials). No longer
                      picked independently, so it can't diverge from the item.
                      Not applicable to aluminium casting: it has its own
                      cascading raw-material pair above. */}
                  {derivesMaterialFromItem && (
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-[#113F67]">
                        {isCutting ? labels.materialSizeLabel : labels.materialLabel} <span className="text-red-500">*</span>
                      </Label>
                      <div className="relative">
                        <Package className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#34699A] z-10 pointer-events-none" />
                        <div
                          className={`${itemInputClass(!!errors.items?.[index]?.size_id)} flex items-center bg-gray-100 text-gray-700`}
                        >
                          {(() => {
                            const materialId = Number(watchedItems?.[index]?.material_id) || 0;
                            const sizeId = Number(watchedItems?.[index]?.size_id) || 0;
                            const materialLabelText = materialOptions.find((o) => o.value === materialId)?.label;
                            const sizeLabelText = materialSizeOptions.find((o) => o.value === sizeId)?.label;
                            return materialLabelText && sizeLabelText ? (
                              `${materialLabelText} (${sizeLabelText})`
                            ) : (
                              <span className="text-gray-400">{labels.selectItemFirst}</span>
                            );
                          })()}
                        </div>
                        <input type="hidden" {...register(`items.${index}.material_id`)} />
                        <input
                          type="hidden"
                          {...register(`items.${index}.size_id`, {
                            validate: (v) => Number(v) > 0 || labels.materialSizeRequired,
                          })}
                        />
                      </div>
                      {errors.items?.[index]?.size_id && (
                        <p className="text-xs text-red-500 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />{labels.materialSizeRequired}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Cutting Size — cutting only. The actual cut length the
                      pipe/leftover math runs on; not the finished item's own
                      Item Size. Remembered per (item, item size) pair and
                      prefilled above, but always editable. */}
                  {isCutting && (
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-[#113F67]">
                        {labels.cuttingSizeLabel} <span className="text-red-500">*</span>
                      </Label>
                      <div className="relative">
                        <Scissors className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#34699A]" />
                        <Input
                          type="number"
                          step="0.1"
                          className={itemInputClass(!!errors.items?.[index]?.cutting_size)}
                          placeholder={labels.cuttingSizePlaceholder}
                          {...register(`items.${index}.cutting_size`, { required: true, valueAsNumber: true, min: 0.001 })}
                          onWheel={(e) => (e.target as HTMLInputElement).blur()}
                        />
                      </div>
                      {errors.items?.[index]?.cutting_size && (
                        <p className="text-xs text-red-500 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />{labels.cuttingSizeRequired}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Weight — not needed for aluminium casting's raw-material qty-only lines */}
                  {requiresWeight && !isAluminiumCasting && (
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-[#113F67]">
                        {labels.weightLabel} <span className="text-red-500">*</span>
                      </Label>
                      <div className="relative">
                        <Weight className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#34699A]" />
                        <Input
                          type="number"
                          step="0.001"
                          className={itemInputClass(!!errors.items?.[index]?.weight)}
                          placeholder="0.000"
                          {...register(`items.${index}.weight`, { required: requiresWeight, valueAsNumber: true, min: 0.001 })}
                          onWheel={(e) => (e.target as HTMLInputElement).blur()}
                        />
                      </div>
                      {errors.items?.[index]?.weight && (
                        <p className="text-xs text-red-500 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />{labels.weightRequired}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Qty */}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-[#113F67]">
                      {labels.qtyLabel} <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <Hash className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#34699A]" />
                      <Input
                        type="number"
                        className={itemInputClass(!!errors.items?.[index]?.qty)}
                        placeholder="0"
                        {...register(`items.${index}.qty`, { required: true, valueAsNumber: true, min: 1 })}
                        onWheel={(e) => (e.target as HTMLInputElement).blur()}
                      />
                    </div>
                    {errors.items?.[index]?.qty && (
                      <p className="text-xs text-red-500 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />{labels.qtyRequired}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}

            <div className="text-right mt-4">
              <Button
                type="button"
                onClick={() => append(emptyItem as any)}
                className="h-[48px] px-6 rounded-lg border-2 border-[#113F67] bg-gradient-to-r from-[#113F67] to-[#34699A] hover:from-[#0d2f4d] hover:to-[#2a547b] text-base"
              >
                <Plus className="h-4 w-4 mr-2" />{labels.addItem}
              </Button>
            </div>
          </div>

          {isCutting && (
            <MaterialGroupsSection
              materialGroups={materialGroups}
              getAvailableLeftoversForGroup={getAvailableLeftoversForGroup}
              selectedLeftoversByGroup={groupSelectedLeftovers}
              onToggleLeftover={toggleGroupLeftover}
            />
          )}

          <div className="border-t-2 border-[#113F67]/10" />

          <div className="flex justify-end space-x-4">
            <Button
              type="button"
              className="h-[48px] px-6 rounded-lg border-2 border-[#34699A] text-[#113F67] hover:bg-[#113F67]/5 bg-white text-base"
              onClick={() => navigate(listRoute)}
              disabled={isLoading}
            >
              {labels.cancelButton}
            </Button>
            <Button
              type="submit"
              disabled={isLoading || subProcessModeConflict}
              className="h-[48px] px-6 rounded-lg border-2 border-[#113F67] bg-gradient-to-r from-[#113F67] to-[#34699A] hover:from-[#0d2f4d] hover:to-[#2a547b] text-base"
            >
              {isLoading ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{labels.saving}</>
              ) : (
                editId ? labels.editButtonLabel : labels.saveButtonLabel
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </form>
  );
};

export default ProcessOrderForm;
